import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { OpenInventoryDiscrepancySkuBadge } from "@/components/manufacturing/OpenInventoryDiscrepancySkuBadge";
import { apiFetch, ApiError } from "@/lib/api";
import type { MrpSkuDetail, MrpSkuMasterRow } from "@/lib/mrp";
import { skuPickerDescriptionLine, skuPickerTitleLine } from "@/lib/mrpSkuPickerDisplay";
import { useOpenInventoryDiscrepancySkus } from "@/lib/useOpenInventoryDiscrepancySkus";
import { Input } from "@/components/ui/Input";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";
import { computeAnchoredListPanel, FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 140;
const MIN_QUERY_LENGTH = 1;
const LIST_MAX_HEIGHT_PX = 224;
const PANEL_GAP_PX = 4;
const VIEWPORT_EDGE_PX = 8;
const COMBOBOX_MIN_WIDTH_PX = 192;
const BLUR_COMMIT_DELAY_MS = 0;

function getScrollParents(node: HTMLElement | null): HTMLElement[] {
  const parents: HTMLElement[] = [];
  let el = node?.parentElement;
  while (el) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const overflow = style.overflow;
    if (
      /(auto|scroll|overlay)/.test(overflowY) ||
      /(auto|scroll|overlay)/.test(overflowX) ||
      /(auto|scroll|overlay)/.test(overflow)
    ) {
      parents.push(el);
    }
    el = el.parentElement;
  }
  return parents;
}

type SkuListResponse = {
  skus: MrpSkuMasterRow[];
  page: number;
  page_size: number;
  total_count: number;
};

/**
 * Trailing debounce while the dropdown stays open without the long dead time typical of naive debouncing:
 * - When the panel opens, use the latest text immediately so an existing SKU or pasted value starts loading at once.
 * - When typed text crosses empty → searchable (or searchable → empty), update immediately so the first character
 *   and clears do not wait for trailing delay before hitting the server.
 * - Other edits reuse a short trailing debounce so we do not request on every keystroke.
 */
function useOpenAwareSkuSearchDebounce(trimmedQuery: string, panelOpen: boolean, delayMs: number, minLength: number): string {
  const [debounced, setDebounced] = useState(trimmedQuery);
  const timerRef = useRef<number | null>(null);
  const prevTrimmedRef = useRef(trimmedQuery);
  const prevOpenRef = useRef(panelOpen);

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = panelOpen;

    if (!panelOpen) {
      return;
    }

    const prevTrimmed = prevTrimmedRef.current;
    prevTrimmedRef.current = trimmedQuery;

    if (!wasOpen) {
      setDebounced(trimmedQuery);
      return;
    }

    const crossesToSearchable = prevTrimmed.length < minLength && trimmedQuery.length >= minLength;
    const crossesBelowSearchable = prevTrimmed.length >= minLength && trimmedQuery.length < minLength;

    if (crossesToSearchable || crossesBelowSearchable) {
      setDebounced(trimmedQuery);
      return;
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setDebounced(trimmedQuery);
    }, delayMs);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [trimmedQuery, panelOpen, delayMs, minLength]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  return debounced;
}

function skuPickerSubtitle(row: MrpSkuMasterRow): string {
  return skuPickerDescriptionLine(row);
}

/** Single-line description hint for help text (matches list row subtitle under the SKU). */
export function mrpSkuMasterRowDescriptionLine(row: MrpSkuMasterRow): string {
  return skuPickerSubtitle(row);
}

function optionDomId(listboxId: string, rowIndex: number): string {
  return `${listboxId}-option-${String(rowIndex)}`;
}

/**
 * Text field with a dropdown of `public.sku` rows from GET `/api/mrp/skus`, filtered by what the user types.
 *
 * Locked (`enforceCatalogSku`): blur picks the arrow- or hover-highlighted row; if nothing is highlighted, the first
 * listed row is accepted when the dropdown has results before falling through to typed validation.
 */
export function AssemblySkuCombobox({
  value,
  onChange,
  id,
  placeholder = "Search or type SKU…",
  className,
  disabled = false,
  onPickMasterRow,
  onBlurCommittedWithSku,
  enforceCatalogSku = false,
  linkOpenDiscrepancy = true,
}: {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** When the user picks a row from the list (not manual typing only). */
  onPickMasterRow?: (row: MrpSkuMasterRow) => void;
  /** Blur with the trimmed field value after list commit or typed validation completes. */
  onBlurCommittedWithSku?: (skuTrimmed: string) => void;
  /**
   * When true, the field may only hold a catalog SKU: exact match in the current result list, validation
   * through GET `/api/mrp/skus/:sku`, or a row chosen from the list. Invalid text is cleared on blur before
   * `onBlurCommittedWithSku` runs.
   */
  enforceCatalogSku?: boolean;
  /** When false, an open-discrepancy badge stays on the field and does not navigate away. */
  linkOpenDiscrepancy?: boolean;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = `${inputId}-listbox`;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxPortalRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const blurTimerRef = useRef<number | null>(null);
  /** True while a pointer pick is committing so deferred blur ignores duplicate commits. */
  const skipBlurCommitRef = useRef(false);

  const [open, setOpen] = useState(false);
  const touchListPick = useTouchListPickSession(open);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  /** Mirrors keyboard highlight before React commits; never trust render-time state alone for deferred blur reads. */
  const highlightedIndexRef = useRef<number | null>(null);
  /** Set only for keyboard highlight so hover and scrollbar drags do not call scrollIntoView. */
  const scrollHighlightIntoViewRef = useRef(false);

  const setHighlightSynchronously = useCallback((next: number | null) => {
    highlightedIndexRef.current = next;
    setHighlightedIndex(next);
  }, []);

  const [panelBox, setPanelBox] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const trimmedValue = value.trim();
  const debouncedQ = useOpenAwareSkuSearchDebounce(trimmedValue, open, DEBOUNCE_MS, MIN_QUERY_LENGTH);
  const queryOk = debouncedQ.length >= MIN_QUERY_LENGTH;

  const skusQuery = useQuery<SkuListResponse, ApiError>({
    queryKey: ["mrp", "skus", "assembly-combobox", debouncedQ],
    queryFn: () =>
      apiFetch<SkuListResponse>("/api/mrp/skus", {
        query: {
          q: debouncedQ,
          page_size: String(PAGE_SIZE),
          page: "1",
        },
      }),
    enabled: open && queryOk,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(
    () =>
      (skusQuery.data?.skus ?? []).filter(
        (row): row is MrpSkuMasterRow => row != null && typeof row.sku === "string",
      ),
    [skusQuery.data],
  );
  const totalCount = skusQuery.data?.total_count;

  const skusForDiscrepancyLookup = useMemo(() => {
    const list = rows.map((row) => row.sku);
    if (trimmedValue !== "") {
      list.push(trimmedValue);
    }
    return list;
  }, [rows, trimmedValue]);
  const { hasOpenDiscrepancy } = useOpenInventoryDiscrepancySkus(skusForDiscrepancyLookup);

  const blurCommitGenerationRef = useRef(0);

  const pickRowSynchronously = useCallback(
    (row: MrpSkuMasterRow) => {
      onChange(row.sku);
      onPickMasterRow?.(row);
      setHighlightSynchronously(null);
      setOpen(false);
    },
    [onChange, onPickMasterRow, setHighlightSynchronously],
  );

  /** Catalog validation when blur does not settle on a list row choice. */
  const handleBlurValidateTypedCatalog = useCallback(
    (raw: string, generation: number) => {
      const trimmed = raw.trim();
      if (!enforceCatalogSku) {
        onBlurCommittedWithSku?.(trimmed);
        return;
      }
      void (async () => {
        if (!trimmed) {
          if (generation !== blurCommitGenerationRef.current) return;
          onChange("");
          onBlurCommittedWithSku?.("");
          return;
        }
        const fromList = rows.find((r) => r.sku === trimmed);
        if (fromList) {
          const canonical = fromList.sku;
          if (canonical !== value) {
            onChange(canonical);
          }
          if (generation !== blurCommitGenerationRef.current) return;
          onBlurCommittedWithSku?.(canonical.trim());
          return;
        }
        try {
          const detail = await apiFetch<MrpSkuDetail>("/api/mrp/skus", { query: { sku: trimmed } });
          if (generation !== blurCommitGenerationRef.current) return;
          const canonical = detail.sku.trim();
          if (canonical !== value) {
            onChange(canonical);
          }
          onBlurCommittedWithSku?.(canonical);
        } catch {
          if (generation !== blurCommitGenerationRef.current) return;
          onChange("");
          onBlurCommittedWithSku?.("");
        }
      })();
    },
    [enforceCatalogSku, onBlurCommittedWithSku, onChange, rows, value],
  );

  /**
   * Same row resolution as deferred blur commit: keyboard/hover highlight first, then catalog
   * fallbacks when `enforceCatalogSku` (exact typed match, else first listed row).
   */
  const resolveListRowPick = useCallback(
    (rawValue: string): MrpSkuMasterRow | null => {
      const idx = highlightedIndexRef.current;
      if (typeof idx === "number" && idx >= 0 && idx < rows.length) {
        return rows[idx]!;
      }
      if (!enforceCatalogSku || rows.length === 0 || rawValue.trim() === "") {
        return null;
      }
      const alreadyPicked = rows.find(
        (row) => row.sku.trim().toLowerCase() === rawValue.trim().toLowerCase(),
      );
      if (alreadyPicked) {
        return alreadyPicked;
      }
      return rows[0]!;
    },
    [enforceCatalogSku, rows],
  );

  const commitResolvedListRowPick = useCallback(
    (rawValue: string): boolean => {
      const rowPick = resolveListRowPick(rawValue);
      if (!rowPick) {
        return false;
      }
      const trimmedPick = rowPick.sku.trim();
      const trimmedRaw = rawValue.trim();
      if (
        trimmedRaw !== "" &&
        trimmedPick.toLowerCase() === trimmedRaw.toLowerCase() &&
        trimmedPick === value.trim()
      ) {
        onBlurCommittedWithSku?.(trimmedPick);
        setHighlightSynchronously(null);
        setOpen(false);
        return true;
      }
      pickRowSynchronously(rowPick);
      onBlurCommittedWithSku?.(trimmedPick);
      return true;
    },
    [onBlurCommittedWithSku, pickRowSynchronously, resolveListRowPick, setHighlightSynchronously, value],
  );

  const runDeferredBlurCommit = useCallback(() => {
    if (blurTimerRef.current != null) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }

    blurTimerRef.current = window.setTimeout(() => {
      blurTimerRef.current = null;

      if (skipBlurCommitRef.current || touchListPick.shouldYieldBlurCommit()) {
        return;
      }

      const rawNow = inputRef.current?.value ?? value;

      if (commitResolvedListRowPick(rawNow)) {
        return;
      }

      const generation = ++blurCommitGenerationRef.current;
      handleBlurValidateTypedCatalog(rawNow, generation);
      setHighlightSynchronously(null);
      setOpen(false);
    }, BLUR_COMMIT_DELAY_MS);
  }, [
    commitResolvedListRowPick,
    handleBlurValidateTypedCatalog,
    value,
    setHighlightSynchronously,
  ]);

  const updatePanelPosition = (): void => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const box = computeAnchoredListPanel({
      anchorRect: input.getBoundingClientRect(),
      gap: PANEL_GAP_PX,
      edge: VIEWPORT_EDGE_PX,
      minWidth: COMBOBOX_MIN_WIDTH_PX,
      chromeHeight: 0,
      listMaxHeightCap: LIST_MAX_HEIGHT_PX,
    });
    setPanelBox({
      top: box.top,
      left: box.left,
      width: box.width,
      maxHeight: box.listContentMaxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    updatePanelPosition();
    const onLayout = (event: Event): void => {
      const listbox = listboxPortalRef.current;
      if (listbox != null && (event.target === listbox || listbox.contains(event.target as Node))) {
        return;
      }
      updatePanelPosition();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    const scrollParents = getScrollParents(inputRef.current);
    for (const parent of scrollParents) {
      parent.addEventListener("scroll", onLayout, { passive: true });
    }
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
      for (const parent of scrollParents) {
        parent.removeEventListener("scroll", onLayout);
      }
    };
  }, [open, debouncedQ, rows.length, skusQuery.isPending, skusQuery.isError, skusQuery.dataUpdatedAt, value]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || listboxPortalRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!scrollHighlightIntoViewRef.current) return;
    scrollHighlightIntoViewRef.current = false;
    const idx = highlightedIndex;
    if (idx == null || idx < 0) return;
    const el = optionRefs.current[idx];
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  useEffect(() => {
    const hi = highlightedIndexRef.current;
    if (hi == null) return;
    if (hi >= rows.length) {
      const nextIndex = rows.length === 0 ? null : rows.length - 1;
      setHighlightSynchronously(nextIndex);
    }
  }, [rows, setHighlightSynchronously]);

  useEffect(
    () => () => {
      if (blurTimerRef.current != null) {
        window.clearTimeout(blurTimerRef.current);
      }
    },
    [],
  );

  /** Selectable SKU rows render as list options (omit loading/error copy). */
  const selectableRows = !(queryOk && !skusQuery.isPending && !skusQuery.isError && rows.length > 0) ? [] : rows;

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  const listboxContent = (
    <>
      {!queryOk ? (
        <p className="px-3 py-2 text-slate-500">Start typing to filter SKUs.</p>
      ) : skusQuery.isPending ? (
        <p className="px-3 py-2 text-slate-500">Loading matches…</p>
      ) : skusQuery.isError ? (
        <p className="px-3 py-2 text-rose-700">{(skusQuery.error as Error).message}</p>
      ) : rows.length === 0 ? (
        <p className="px-3 py-2 text-slate-500">No matching SKUs.</p>
      ) : (
        <>
          {rows.map((row, rowIndex) => {
            const subtitle = skuPickerSubtitle(row);
            const active = highlightedIndex === rowIndex;
            return (
              <button
                key={row.sku}
                ref={(element) => {
                  optionRefs.current[rowIndex] = element;
                }}
                type="button"
                role="option"
                id={optionDomId(listboxId, rowIndex)}
                aria-selected={row.sku === value.trim()}
                className={cn(
                  "flex w-full flex-col px-3 py-1.5 text-left hover:bg-slate-100",
                  row.sku === value.trim() && "bg-slate-50",
                  active && "bg-sky-50 ring-1 ring-inset ring-sky-200",
                )}
                onMouseEnter={() => setHighlightSynchronously(rowIndex)}
                onPointerDown={(event) => {
                  touchListPick.onListItemPointerDown(event, () => {
                    if (blurTimerRef.current != null) {
                      window.clearTimeout(blurTimerRef.current);
                      blurTimerRef.current = null;
                    }
                    skipBlurCommitRef.current = true;
                    pickRowSynchronously(row);
                    onBlurCommittedWithSku?.(row.sku.trim());
                    window.setTimeout(() => {
                      skipBlurCommitRef.current = false;
                    }, 0);
                  });
                }}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="flex min-w-0 flex-col">
                    <span className="font-mono text-xs text-slate-900">{skuPickerTitleLine(row)}</span>
                    {subtitle ? (
                      <span className="line-clamp-2 text-[11px] leading-snug text-slate-500">{subtitle}</span>
                    ) : null}
                  </span>
                  {hasOpenDiscrepancy(row.sku) ? (
                    <OpenInventoryDiscrepancySkuBadge sku={row.sku} linkToQueue={linkOpenDiscrepancy} />
                  ) : null}
                </span>
              </button>
            );
          })}
          {typeof totalCount === "number" && totalCount > rows.length ? (
            <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-500">
              Showing first {rows.length} of {totalCount}. Refine your search to narrow the list.
            </div>
          ) : null}
        </>
      )}
    </>
  );

  return (
    <div ref={rootRef} className={cn("min-w-[12rem]", className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          id={inputId}
          type="text"
          autoComplete="off"
          value={value}
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            open && highlightedIndex != null ? optionDomId(listboxId, highlightedIndex) : undefined
          }
          role="combobox"
          placeholder={placeholder}
          className={cn(
            "h-10 font-mono text-xs",
            !open && trimmedValue !== "" && hasOpenDiscrepancy(trimmedValue) ? "pr-28" : undefined,
          )}
          disabled={disabled}
          onChange={(event) => {
          if (disabled) return;
          const next = event.target.value;
          onChange(next);
          setHighlightSynchronously(null);
          setOpen(true);
        }}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
        }}
        onBlur={() => {
          runDeferredBlurCommit();
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Escape") {
            event.stopPropagation();
            setOpen(false);
            setHighlightSynchronously(null);
            return;
          }
          if (event.key === "ArrowDown") {
            if (!open) setOpen(true);
            if (!queryOk || skusQuery.isPending || selectableRows.length === 0) return;
            event.preventDefault();
            const previous = highlightedIndexRef.current;
            const nextHighlight =
              previous == null ? 0 : previous >= selectableRows.length - 1 ? selectableRows.length - 1 : previous + 1;
            highlightedIndexRef.current = nextHighlight;
            scrollHighlightIntoViewRef.current = true;
            setHighlightedIndex(nextHighlight);
            return;
          }
          if (event.key === "ArrowUp") {
            if (!open) setOpen(true);
            if (!queryOk || skusQuery.isPending || selectableRows.length === 0) return;
            event.preventDefault();
            const previous = highlightedIndexRef.current;
            const nextHighlight =
              previous == null ? selectableRows.length - 1 : previous <= 0 ? 0 : previous - 1;
            highlightedIndexRef.current = nextHighlight;
            scrollHighlightIntoViewRef.current = true;
            setHighlightedIndex(nextHighlight);
            return;
          }
          if (event.key === "Enter") {
            if (!open) return;
            event.preventDefault();
            event.stopPropagation();
            if (!queryOk || skusQuery.isPending) return;
            if (blurTimerRef.current != null) {
              window.clearTimeout(blurTimerRef.current);
              blurTimerRef.current = null;
            }
            const rawNow = inputRef.current?.value ?? value;
            if (selectableRows.length > 0 && commitResolvedListRowPick(rawNow)) {
              return;
            }
            if (enforceCatalogSku) {
              const generation = ++blurCommitGenerationRef.current;
              handleBlurValidateTypedCatalog(rawNow, generation);
              setHighlightSynchronously(null);
              setOpen(false);
            }
          }
        }}
        />
        {!open && trimmedValue !== "" && hasOpenDiscrepancy(trimmedValue) ? (
          <div className="pointer-events-auto absolute right-2 top-1/2 -translate-y-1/2">
            <OpenInventoryDiscrepancySkuBadge sku={trimmedValue} linkToQueue={linkOpenDiscrepancy} />
          </div>
        ) : null}
      </div>
      {open && panelBox != null && portalTarget
        ? createPortal(
            <div
              ref={listboxPortalRef}
              id={listboxId}
              data-floating-combobox-panel
              role="listbox"
              style={{
                position: "fixed",
                top: panelBox.top,
                left: panelBox.left,
                width: panelBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
                maxHeight: panelBox.maxHeight,
              }}
              className="overflow-auto rounded-md border border-slate-200 bg-white py-1 text-left text-xs shadow-lg"
            >
              {listboxContent}
            </div>,
            portalTarget,
          )
        : null}
    </div>
  );
}
